import i18nFactory from '@yandex-int/i18n';
import { Spin } from 'lego-on-react';
import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { TextInputDecorator } from '../../components/TextInputDecorator/TextInputDecorator';
import { Bem } from '../../service/Bem/Bem';
import { PcodeCanvasPreview } from '../PcodeCanvasPreview/PcodeCanvasPreview';

import './IdeasList.css';
import * as keyset from './IdeasList.i18n';
import { CanvasIdea } from './typings';

const i18n = i18nFactory(keyset);

const BLOCK = 'ideas-list';

const PROGRESS_TEXTS = [i18n('progress-message'), i18n('progress-message1'), i18n('progress-message2')];

const IDEA_SIZE = {
    width: 350,
    height: 300
};

const KEYBOARD_ENTER_KEY = 13;

export interface IdeasListProps {
    ideasRequestInProgress?: boolean;
    ideasRequestIsFinished?: boolean;
    hasError?: boolean;
    errorMessage?: string;
    progressStep?: number;
    ideasList: CanvasIdea[];
    url?: string;
    ideaId?: string;
    onUrlChange: (url: string) => void;
    onUrlSubmit: (url: string) => void;
    fetchIdeas: (ideaId: string) => void;
    selectIdea: (ideaId: string, presetId: number, isAdaptive?: boolean) => void;
}

export class IdeasList extends React.PureComponent<IdeasListProps> {
    private static Item = React.memo(
        (props: { idea: CanvasIdea; selectIdea: (presetId: number, isAdaptive?: boolean) => void }) => {
            const { isAdaptive } = props.idea.data.options;
            const data = useMemo(() => (isAdaptive ? { ...props.idea.data, ...IDEA_SIZE } : props.idea.data), [
                props.idea.data,
                isAdaptive
            ]);
            const selectIdea = useCallback(() => props.selectIdea(props.idea.presetId, isAdaptive), [
                props.idea.presetId
            ]);
            const currentSize = useMemo(
                () => ({
                    width: data.width,
                    height: data.height
                }),
                [data]
            );

            return (
                <div className={Bem.cls(BLOCK, 'item', { adaptive: isAdaptive })}>
                    <div className={Bem.cls(BLOCK, 'idea')}>
                        <div className={Bem.cls(BLOCK, 'link')} onClick={selectIdea}>
                            <div className={Bem.cls(BLOCK, 'iframe-blocker')} />
                            <PcodeCanvasPreview pcodeData={data} currentSize={currentSize} />
                        </div>
                        {isAdaptive && (
                            <div className={Bem.cls(BLOCK, 'adaptive-section')}>
                                <h3 className={Bem.cls(BLOCK, 'adaptive-header')}>{i18n('adaptive')}</h3>
                                <p className={Bem.cls(BLOCK, 'adaptive-disclaimer')}>{i18n('adaptive-disclaimer')}</p>
                                <p className={Bem.cls(BLOCK, 'adaptive-disclaimer')}>{i18n('adaptive-info')}</p>
                                <ButtonDecorator
                                    theme={'action'}
                                    type={'link'}
                                    onClick={selectIdea}
                                    text={i18n('apply-adaptive')}
                                    size={'m'}
                                />
                            </div>
                        )}
                    </div>
                </div>
            );
        }
    );

    componentDidMount(): void {
        if (this.props.ideaId) {
            this.props.fetchIdeas(this.props.ideaId);
        }
    }

    render() {
        return (
            <div className={Bem.cls(BLOCK)}>
                {this.renderUrlBlock()}
                {this.renderDisclaimer()}
                <div
                    className={Bem.cls({
                        block: BLOCK,
                        elem: 'items',
                        mods: {
                            progress: this.props.ideasRequestInProgress === true
                        }
                    })}
                >
                    {this.renderIdeasListItems()}
                </div>
            </div>
        );
    }

    private renderDisclaimer() {
        const { ideasRequestIsFinished, ideasRequestInProgress, hasError, errorMessage } = this.props;

        if (ideasRequestInProgress) {
            return null;
        }

        if (hasError) {
            return <div className={Bem.cls(BLOCK, 'error-message')}>{errorMessage}</div>;
        }

        if (!ideasRequestIsFinished) {
            return <div className={Bem.cls(BLOCK, 'empty-message')}>{i18n('empty-message')}</div>;
        }

        return null;
    }

    private renderIdeasListItems() {
        const { ideasList, ideasRequestInProgress, ideasRequestIsFinished, hasError } = this.props;

        if (ideasRequestInProgress) {
            return this.renderIdeasListProgress();
        }

        if (hasError || !ideasRequestIsFinished) {
            return null;
        }

        return ideasList.map((idea, index) => {
            return <IdeasList.Item selectIdea={this.selectIdea} idea={idea} key={`idea_${index}`} />;
        });
    }

    private renderIdeasListProgress(): JSX.Element {
        return (
            <div className={Bem.cls(BLOCK, 'progress-content')}>
                <Spin size={'l'} progress={true} />
                <div className={Bem.cls(BLOCK, 'progress-text')}>{PROGRESS_TEXTS[this.props.progressStep || 0]}</div>
            </div>
        );
    }

    private selectIdea = (presetId: number, isAdaptive?: boolean): void => {
        if (this.props.ideaId) {
            this.props.selectIdea(this.props.ideaId, presetId, isAdaptive);
        }
    };

    private onSubmitClick = () => {
        if (this.props.url) {
            this.props.onUrlSubmit(this.props.url);
        }
    };

    private onUrlChange = (url: string) => {
        this.props.onUrlChange(url);
    };

    private renderUrlBlock(): JSX.Element {
        const { url, ideasRequestInProgress } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'url-block')}>
                <TextInputDecorator
                    autoFocus={true}
                    size={'s'}
                    text={url}
                    theme={'normal'}
                    view={'classic'}
                    onChange={this.onUrlChange}
                    onKeyDown={this.onKeyDown}
                    cls={Bem.cls(BLOCK, 'url-input')}
                    placeholder={i18n('url-placeholder')}
                />
                <ButtonDecorator
                    cls={Bem.cls(BLOCK, 'submit')}
                    progress={ideasRequestInProgress}
                    theme={'action'}
                    size={'s'}
                    text={i18n('create-banners')}
                    disabled={!url || ideasRequestInProgress}
                    onClick={this.onSubmitClick}
                />
            </div>
        );
    }

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        const { ideasRequestInProgress, url } = this.props;
        const { keyCode } = event;

        if (ideasRequestInProgress || !url) {
            return;
        }

        if (keyCode === KEYBOARD_ENTER_KEY) {
            this.onSubmitClick();
        }
    };
}
